/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { memo, useState } from 'react';
import { Chart } from '@scuf/charts';
import { ResizerProps } from '@scuf/charts/dist/types/ResizerProps';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../locales/en-US/descriptors';
import { IPerformanceGraphProps } from './IPerformanceGraph';
import moment from 'moment';
import './TooltipPerformanceGraphCommonTheme.scss';
import {
  DailyTickInterval,
  HourlyTickInterval,
  PERCENTAGE_HUNDRED,
  YAXISCONSUMPTIONMIN,
  singleDayTickInterval
} from '../Utils/Constants';
import {
  getDailyPerformaceGraphData,
  getFormattedConspDetails,
  getFormattedDate,
  getPerformaceGraphData,
  getTickInterval,
  getTooltipValue
} from '../Utils/PerformanceGraphUtils';
import { shouldNotUpdate } from '../Utils/Utils';
import {
  getMaxYAxisValue,
  getYAxisLabel
} from '../Utils/PortfolioEnergy/PortfolioEnergyVDUtils';
import { IAny } from '../../DashboardKPIWidgets/IEnergyManagementProps';
import { ConversionType } from '../Utils/unitHandling/UnitModel';
import _ from 'lodash';
import axisTickPositioner from '../Utils/axisTickPositioner';
import { Axis, AxisTickPositionerCallbackFunction } from 'highcharts';
import { determineInterval } from '../Utils/determineInterval';
import { EnergyUsageDataSourceType } from '../Enums';

export const PerformanceGraph = memo((props: IPerformanceGraphProps) => {
  const [t, lng] = useTranslation();
  const d = descriptors.energyManagement;
  const {
    chartSeries,
    chartSeries: { consumptionUnitDisplay },
    startTime,
    endTime,
    height,
    spacing,
    aggregationType,
    removePadding,
    conversionType
  } = props;

  let {
    actualConsumptionData,
    baselineConsumptionData,
    savedActuals,
    savedBaseline
  } = getFormattedConspDetails(chartSeries);
  const renderTooltip = (details: IAny) => {
    const { x, y, label } = details;
    return (
      <div className="tooltip-top">
        <div className="tooltip-pane">
          {label == t(d.ACTUAL) ? (
            <>
              <div className="tooltip-date emaasThemeColor">
                {getFormattedDate(aggregationType, x, lng)}
              </div>
              <div className="tooltip-pane-item">
                <div className="tooltip-heading-container">
                  <span
                    className="dot"
                    style={{ backgroundColor: '#1272B2' }}
                  ></span>
                  <span className="tooltip-header-label emaasThemeColor">
                    {t(d.ACTUAL)}
                  </span>
                </div>
                <div>
                  <span className="tooltip-header-value emaasThemeColor">
                    {getTooltipValue(
                      x,
                      y,
                      savedActuals,
                      consumptionUnitDisplay
                    )}
                  </span>
                </div>
              </div>
            </>
          ) : (
            ' '
          )}
          {label == t(d.BASELINE) ? (
            <div>
              <div className="tooltip-pane-item">
                <div className="tooltip-heading-container">
                  <span
                    className="dot"
                    style={{ backgroundColor: '#606061' }}
                  ></span>
                  <span className="tooltip-header-label emaasThemeColor">
                    {t(d.BASELINE)}{' '}
                  </span>
                </div>
                <div>
                  <span className="tooltip-header-value emaasThemeColor">
                    {getTooltipValue(
                      x,
                      y,
                      savedBaseline,
                      consumptionUnitDisplay
                    )}
                  </span>
                </div>
              </div>
              {props.dataSourceType ===
              EnergyUsageDataSourceType.UTILITY_METER ? (
                <p className="energy-tooltip-desc-utility">
                  {t(d.ENERGYUTILITYGRAPHTOOLTIP)}
                </p>
              ) : (
                // <p className="energy-tooltip-desc-live">
                //   {' '}
                //   {t(d.ENERGYLIVEGRAPHTOOLTIP)}{' '}
                // </p>
                ''
              )}
            </div>
          ) : (
            ''
          )}
          {label == t(d.PREVIOUSPERIOD) ? (
            <div>
              <div className="tooltip-pane-item">
                <div className="tooltip-heading-container">
                  <span
                    className="dot"
                    style={{ backgroundColor: '#606061' }}
                  ></span>
                  <span className="tooltip-header-label emaasThemeColor">
                    {t(d.PREVIOUSPERIOD)}{' '}
                  </span>
                </div>
                <div>
                  <span className="tooltip-header-value emaasThemeColor">
                    {getTooltipValue(
                      x,
                      y,
                      savedBaseline,
                      consumptionUnitDisplay
                    )}
                  </span>
                </div>
              </div>
              {props.dataSourceType ===
              EnergyUsageDataSourceType.UTILITY_METER ? (
                <p className="energy-tooltip-desc-utility">
                  {t(d.ENERGYUTILITYGRAPHTOOLTIP)}
                </p>
              ) : (
                // <p className="energy-tooltip-desc-live">
                //   {' '}
                //   {t(d.ENERGYLIVEGRAPHTOOLTIP)}{' '}
                // </p>

                ''
              )}
            </div>
          ) : (
            ''
          )}
        </div>
      </div>
    );
  };

  const reSizerProps: ResizerProps = {
    refreshRate: PERCENTAGE_HUNDRED,
    refreshMode: 'debounce',
    handleHeight: true
  };

  const getApplicableLabel = (value: number, interval: number): string => {
    if (interval === DailyTickInterval) {
      return moment(value)
        .locale(lng)
        .format('MMM');
    } else if (interval === HourlyTickInterval) {
      return moment(value)
        .locale(lng)
        .format('hA');
    } else if (interval === singleDayTickInterval) {
      return moment(value)
        .locale(lng)
        .date()
        .toString();
    } else {
      return moment(value)
        .locale(lng)
        .format('MMM');
    }
  };

  const dataSeriesSettings = [];
  if (aggregationType.toLowerCase() == 'hourly') {
    ({
      baselineConsumptionData,
      actualConsumptionData
    } = getPerformaceGraphData(
      startTime,
      baselineConsumptionData,
      actualConsumptionData
    ));
  } else {
    ({
      baselineConsumptionData,
      actualConsumptionData
    } = getDailyPerformaceGraphData(
      startTime,
      endTime,
      baselineConsumptionData,
      actualConsumptionData
    ));
  }

  if (actualConsumptionData) {
    const element = (
      <Chart.Line
        key={t(d.ACTUAL)}
        name={t(d.ACTUAL)}
        data={actualConsumptionData}
        marker={{ enabled: false }}
        dashStyle="Solid"
        spline={true}
        index={undefined}
        id={'0'}
        connectNulls={true}
      />
    );
    dataSeriesSettings.push({
      key: t(d.ACTUALKEY),
      color: '#1272b2',

      element: element,
      legendItem: {
        text: t(d.ACTUAL),
        color: '#1272b2',
        enabled: true,
        className: 'legend-item-actual'
      }
    });
  }

  if (baselineConsumptionData) {
    const element = (
      <Chart.Line
        key={t(d.BASELINE)}
        name={
          _.toLower(conversionType) ===
          ConversionType.WATER.toString().toLowerCase()
            ? t(d.PREVIOUSPERIOD)
            : t(d.BASELINE)
        }
        data={baselineConsumptionData}
        marker={{ enabled: false }}
        dashStyle="Dash"
        spline={true}
        index={undefined}
        id={'1'}
        connectNulls={true}
      />
    );
    dataSeriesSettings.push({
      key: t(d.BASELINEKEY),
      color: '#606061',

      element: element,
      legendItem: {
        text: t(d.ACTUAL),
        color: '#606061',
        enabled: true,
        className: 'legend-item-baseline'
      }
    });
  }

  const dateValueArray = actualConsumptionData.map((actualData: number[]) => {
    return actualData[0];
  });
  const axisMin = Math.min(...dateValueArray);
  const axisMax = Math.max(...dateValueArray);
  const tickInterval = getTickInterval(startTime, endTime, aggregationType);
  const yAxisTickInterval = getMaxYAxisValue(
    actualConsumptionData,
    baselineConsumptionData
  );
  const YAxisProps = {
    gridLineColor: '#505050',
    gridLineWidth: 1,
    className: 'chart-widget-yaxis',
    tickAmount: 4,
    tickInterval: yAxisTickInterval,
    allowDecimals: true
  };
  let numOfDays = -1;
  if (axisMin !== undefined && axisMax !== undefined) {
    numOfDays = moment(axisMax).diff(axisMin, 'days');
  }
  const interval = determineInterval(numOfDays);
  const axisFormatter: AxisTickPositionerCallbackFunction = function(
    this: Axis
  ) {
    return axisTickPositioner(
      interval,
      moment(axisMin),
      moment(axisMax),
      // @ts-ignore
      this.len
    );
  };
  return (
    <div className="emaas-performance-widget-container">
      {
        <div className="performance-widget-container">
          <Chart
            height={props.optimusSubscribed ? 120 : height}
            spacing={spacing}
            colors={dataSeriesSettings.map((item) => item.color)}
            showScrollbar={false}
            ResizerProps={reSizerProps}
            chartType="basic"
            className="energy-comparison-chart"
          >
            {dataSeriesSettings.map((itm) => itm.element)}
            {aggregationType !== 'Hourly' ? (
              <Chart.XAxis
                type="datetime"
                tickLength={0}
                className="chart-widget-xaxis"
                tickInterval={tickInterval}
                labels={{
                  align: 'center',
                  formatter() {
                    return getApplicableLabel(this.value, tickInterval);
                  }
                }}
                min={axisMin}
                max={axisMax}
                tickPositioner={axisFormatter}
              />
            ) : (
              <Chart.XAxis
                type="datetime"
                tickLength={0}
                className="chart-widget-xaxis"
                tickInterval={tickInterval}
                labels={{
                  align: 'center',
                  formatter() {
                    return getApplicableLabel(this.value, tickInterval);
                  }
                }}
                min={axisMin}
                max={axisMax}
                // tickPositioner={axisFormatter}
              />
            )}

            <Chart.YAxis
              showLastLabel={true}
              min={YAXISCONSUMPTIONMIN}
              {...YAxisProps}
              labels={{
                formatter() {
                  return getYAxisLabel(consumptionUnitDisplay, this.value);
                }
              }}
            />
            <Chart.Tooltip
              borderRadius={10}
              valueSuffix={consumptionUnitDisplay}
              renderContent={renderTooltip}
              backgroundColor="white"
              shared={true}
            />
            <Chart.Legend enabled={false} />
          </Chart>
        </div>
      }
      {
        <div
          className={`${removePadding ? 'legends removePadding' : 'legends'}`}
        >
          <div className="flex-container">
            <div className="legend-baseline">
              {_.toLower(conversionType) ===
              ConversionType.WATER.toString().toLowerCase()
                ? t(d.PREVIOUSPERIOD)
                : t(d.BASELINE)}
            </div>
            <div className="legend-actual-Border">
              <div className="legend-actual">{t(d.ACTUAL)}</div>
            </div>
          </div>
        </div>
      }
    </div>
  );
}, shouldNotUpdate);

export default PerformanceGraph;
